import { Role, type Room } from '@infiltre/shared';
import { motion } from 'motion/react';
import { type MessageKey, useT } from '@/shared/lib/i18n';
import { mugshotForPlayer } from '@/shared/lib/mugshot';
import { roleTint, roleTintTextColor } from '@/shared/lib/role';
import { Button, MugshotPortrait, PaperSheet, Pin, Stage } from '@/shared/ui';
import { ArrowRight, BackArrow } from '@/shared/ui/icons';

export type FinishedScreenProps = {
  room: Room;
  onContinue: () => void;
  continueLabel: string;
  onRematch?: () => void;
  onShowRecap?: () => void;
  recapCount?: number;
};

const TILTS = [-3, 2, -1.5, 2.5, -2.5, 3];

const fileTagFromId = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return `F-${String(h % 1000).padStart(3, '0')}`;
};

/** Game-over verdict — cork stage + dossier paper title card with AFFAIRE
 *  CLOSE stamp, then mugshot polaroid grid with role-tinted role chips,
 *  then orange replay + dark "back home" CTAs. Mirrors the verdict
 *  template (PhaseHeader paper-dossier + polaroid pattern from VoteBoard).
 */
export const FinishedScreen = ({
  room,
  onContinue,
  continueLabel,
  onRematch,
  onShowRecap,
  recapCount = 0,
}: FinishedScreenProps) => {
  const t = useT();
  const tint = roleTint(room.winner ?? undefined);
  const titleParts = (() => {
    if (room.winner === Role.Flic)
      return { team: t('round.result.win_team.flic'), verb: t('round.result.win_verb.flic') };
    if (room.winner === Role.Infiltre)
      return {
        team: t('round.result.win_team.infiltre'),
        verb: t('round.result.win_verb.infiltre'),
      };
    if (room.winner === Role.Clochard)
      return {
        team: t('round.result.win_team.clochard'),
        verb: t('round.result.win_verb.clochard'),
      };
    return { team: t('round.result.title_finished'), verb: '' };
  })();
  const sub = (() => {
    if (room.winner === Role.Flic) return t('round.result.sub_flic');
    if (room.winner === Role.Infiltre) return t('round.result.sub_infiltre');
    if (room.winner === Role.Clochard) return t('round.result.sub_clochard');
    return '';
  })();

  return (
    <Stage tone="board">
      <main className="mx-auto flex h-full w-full max-w-md flex-col">
        <div className="relative px-4 pt-4">
          <motion.div
            initial={{ opacity: 0, y: -6, rotate: 0 }}
            animate={{ opacity: 1, y: 0, rotate: -1.2 }}
            transition={{ duration: 0.4 }}
            className="paper-dossier relative px-5 pt-4 pb-3"
          >
            <Pin left={28} top={-6} />
            <Pin left={'calc(100% - 28px)'} top={-6} />
            <div className="type text-[10px] text-ink/55 uppercase tracking-[0.22em]">
              {t('round.result.complete_eyebrow').toUpperCase()}
              {' · '}
              {String(room.round).padStart(1, '0')}{' '}
              {room.round === 1 ? t('round.distribution.tour') : `${t('round.distribution.tour')}S`}
            </div>
            <h1
              className="mt-1 font-display font-extrabold text-[32px] uppercase italic leading-[0.95] sm:text-[40px]"
              style={{ letterSpacing: '-0.01em' }}
            >
              <span style={{ color: tint }}>{titleParts.team}</span>
              {titleParts.verb && (
                <>
                  {' '}
                  <span style={{ color: 'var(--color-ink)' }}>{titleParts.verb}</span>
                </>
              )}
            </h1>
            {sub && (
              <p
                className="mt-1.5 font-hand text-[15px] italic leading-[1.35]"
                style={{ color: 'var(--color-magenta)' }}
              >
                {sub}
              </p>
            )}
            <motion.div
              initial={{ rotate: -22, scale: 1.7, opacity: 0 }}
              animate={{ rotate: -8, scale: 1, opacity: 0.92 }}
              transition={{ duration: 0.5, delay: 0.3, ease: [0.5, 1.6, 0.5, 1] }}
              className="absolute top-3 right-3 whitespace-nowrap px-2 py-1 text-center font-display font-extrabold text-[10px] uppercase leading-[1.05] tracking-[0.16em]"
              style={{
                border: `2px solid var(--color-stamp-red)`,
                color: 'var(--color-stamp-red)',
                background: 'rgba(244,241,232,.55)',
              }}
            >
              {t('round.case_closed').toUpperCase()}
            </motion.div>
          </motion.div>
        </div>

        <div className="flex flex-1 flex-col px-5 pt-5 pb-3">
          <div className="font-hand text-[18px] text-cream/85 italic lowercase">
            {t('round.result.cast_eyebrow')} · {room.players.length}{' '}
            {t('round.result.suspects_count')}
          </div>
          <ul className="mt-4 grid grid-cols-3 gap-x-3 gap-y-5">
            {room.players.map((p, i) => {
              const tilt = TILTS[i % TILTS.length]!;
              const initial = p.name.charAt(0).toUpperCase();
              const playerTint = roleTint(p.role);
              const chipText = roleTintTextColor(p.role);
              return (
                <li key={p.id} className="flex flex-col items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 6, rotate: 0 }}
                    animate={{ opacity: 1, y: 0, rotate: tilt }}
                    transition={{ duration: 0.32, delay: 0.06 * i }}
                    className="w-full"
                  >
                    <PaperSheet
                      tone="cream"
                      seed={p.id}
                      className="px-1.5 pt-1.5 pb-2"
                      style={{ boxShadow: 'var(--shadow-paper-soft)' }}
                    >
                      <div className="relative aspect-[4/5] w-full overflow-hidden border border-ink/15">
                        <MugshotPortrait config={mugshotForPlayer(p)} />
                        <span className="absolute top-1 left-1.5 font-mono text-[8px] text-ink/55 tracking-[0.2em]">
                          #{String(p.order ?? i + 1).padStart(3, '0')}
                        </span>
                        <span className="absolute right-1 bottom-1 font-mono text-[8px] text-ink/55 tracking-[0.18em]">
                          {fileTagFromId(p.id)}
                        </span>
                        <span className="absolute right-1.5 bottom-1 translate-y-[-12px] font-mono text-[10px] text-ink/55">
                          {initial}
                        </span>
                      </div>
                      <div className="mt-1 truncate text-center font-hand text-[16px] text-ink leading-tight">
                        {p.name}
                      </div>
                    </PaperSheet>
                  </motion.div>
                  {p.role && (
                    <span
                      className="-mt-1 relative font-display font-extrabold text-[10px] uppercase tracking-[0.14em]"
                      style={{
                        background: playerTint,
                        color: chipText,
                        border: `1.5px solid ${playerTint}`,
                        padding: '2px 8px',
                        boxShadow: '1.5px 1.5px 0 rgba(0,0,0,.3)',
                        transform: `rotate(${tilt * 0.6}deg)`,
                      }}
                    >
                      {t(`role.${p.role}` as MessageKey)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-col gap-2 px-5 pt-2 pb-5">
          {onRematch ? (
            <>
              <Button variant="orange" onClick={onRematch} trailing={<ArrowRight />}>
                {t('round.result.replay_cta')}
              </Button>
              {onShowRecap && recapCount > 0 && (
                <Button variant="ghost-light" onClick={onShowRecap}>
                  {recapCount === 1
                    ? t('round.result.view_case_log_one')
                    : t('round.result.view_case_log_many', { count: String(recapCount) })}
                </Button>
              )}
              <Button variant="ink" onClick={onContinue} leading={<BackArrow />}>
                {t('round.result.new_dossier_cta')}
              </Button>
            </>
          ) : (
            <Button variant="orange" onClick={onContinue} trailing={<ArrowRight />}>
              {continueLabel}
            </Button>
          )}
        </div>
      </main>
    </Stage>
  );
};
